import {
    ClientType,
    HierarchyRow,
    OrganizationContext,
} from "models/ClientBrain/OrganizationContext";
import { ClassifierStatus } from "models/ClientBrain/ClassifierStatus";
import {
    ClientBrainUserSummary,
    UserContextProfile,
} from "models/ClientBrain/UserContext";
import {
    GlossaryEntry,
    GlossaryEntryPayload,
} from "models/ClientBrain/GlossaryEntry";
import BaseService from "./BaseService";

export interface ProgramEventPayload {
    startDate: string;
    description: string;
}

export interface UpdateUserContextPayload {
    role: string;
    goals: { id?: string; text: string }[];
    responsibilities: { id?: string; text: string }[];
    successMetrics: { id?: string; text: string }[];
    currentConcerns: { id?: string; text: string }[];
}

export interface UpdateHierarchyPayload {
    id: string;
    description: string;
    therapeuticAreaIds: string[];
}

export interface UpdateClassifierDescriptionPayload {
    id: string;
    description: string;
}

/** Client for the ClientBrainController (api/ClientBrain). */
export class ClientBrainService extends BaseService {
    protected urlBase = "api/ClientBrain";

    /**
     * GET that tolerates an empty response body (some endpoints serialize "no data"
     * as an empty 200, which would otherwise throw on response.json()).
     */
    private async getJson<T>(url: string, fallback: T): Promise<T> {
        const res = await this.get<Response>(
            new URLSearchParams(),
            url,
            undefined,
            undefined,
            true,
        );
        const text = await res.text();
        return text ? (JSON.parse(text) as T) : fallback;
    }

    // --- Organization context ---

    public getOrganizationContext(): Promise<OrganizationContext | null> {
        return this.getJson<OrganizationContext | null>(
            "organization-context",
            null,
        );
    }

    public setClientType(clientType: ClientType): Promise<OrganizationContext> {
        return this.put(
            new URLSearchParams(),
            "organization-context/client-type",
            JSON.stringify({ clientType }),
        );
    }

    public setGoals(value: string): Promise<OrganizationContext> {
        return this.put(
            new URLSearchParams(),
            "organization-context/goals",
            JSON.stringify({ value }),
        );
    }

    public addProgramEvent(
        payload: ProgramEventPayload,
    ): Promise<OrganizationContext> {
        return this.post(
            JSON.stringify(payload),
            new URLSearchParams(),
            "organization-context/program-events",
        );
    }

    public editProgramEvent(
        id: string,
        payload: ProgramEventPayload,
    ): Promise<OrganizationContext> {
        return this.put(
            new URLSearchParams(),
            `organization-context/program-events/${id}`,
            JSON.stringify(payload),
        );
    }

    public async deleteProgramEvent(id: string): Promise<OrganizationContext> {
        const res = await this.delete(
            new URLSearchParams(),
            `organization-context/program-events/${id}`,
        );
        return res.json();
    }

    // --- Hierarchies ---

    public getHierarchies(): Promise<HierarchyRow[]> {
        return this.getJson<HierarchyRow[]>("hierarchies", []);
    }

    public updateHierarchyDescription(
        id: string,
        description: string,
    ): Promise<HierarchyRow> {
        return this.put(
            new URLSearchParams(),
            `hierarchies/${id}/description`,
            JSON.stringify({ description }),
        );
    }

    public updateHierarchyTherapeuticAreas(
        id: string,
        therapeuticAreaIds: string[],
    ): Promise<HierarchyRow> {
        return this.put(
            new URLSearchParams(),
            `hierarchies/${id}/therapeutic-areas`,
            JSON.stringify({ therapeuticAreaIds }),
        );
    }

    public updateHierarchies(
        updates: UpdateHierarchyPayload[],
    ): Promise<HierarchyRow[]> {
        return this.put(
            new URLSearchParams(),
            "hierarchies",
            JSON.stringify({ updates }),
        );
    }

    // --- User context ---

    public getUserContexts(): Promise<UserContextProfile[]> {
        return this.getJson<UserContextProfile[]>("user-contexts", []);
    }

    public getEligibleUsers(): Promise<ClientBrainUserSummary[]> {
        return this.getJson<ClientBrainUserSummary[]>(
            "user-contexts/eligible-users",
            [],
        );
    }

    public addUserContext(userId: string): Promise<UserContextProfile> {
        return this.post(
            null,
            new URLSearchParams(),
            `user-contexts/${userId}`,
        );
    }

    public updateUserContext(
        userId: string,
        payload: UpdateUserContextPayload,
    ): Promise<UserContextProfile> {
        return this.put(
            new URLSearchParams(),
            `user-contexts/${userId}`,
            JSON.stringify(payload),
        );
    }

    public deleteUserContext(userId: string): Promise<Response> {
        return this.delete(new URLSearchParams(), `user-contexts/${userId}`);
    }

    // --- Self-service (User Settings tab) ---

    public getMyUserContext(): Promise<UserContextProfile> {
        return this.get(new URLSearchParams(), "user-contexts/me");
    }

    public updateMyUserContext(
        payload: UpdateUserContextPayload,
    ): Promise<UserContextProfile> {
        return this.put(
            new URLSearchParams(),
            "user-contexts/me",
            JSON.stringify(payload),
        );
    }

    // --- Classifiers (read-only) ---

    public getClassifiers(): Promise<
        Record<string, ClassifierStatus[] | ClassifierStatus>
    > {
        return this.getJson<Record<string, ClassifierStatus[] | ClassifierStatus>>(
            "classifiers",
            {},
        );
    }

    public updateClassifierDescriptions(
        updates: UpdateClassifierDescriptionPayload[],
    ): Promise<Record<string, ClassifierStatus[]>> {
        return this.put(
            new URLSearchParams(),
            "classifiers",
            JSON.stringify({ updates }),
        );
    }

    // --- Glossary ---

    public getGlossary(): Promise<GlossaryEntry[]> {
        return this.getJson<GlossaryEntry[]>("glossary", []);
    }

    public saveGlossary(
        entries: GlossaryEntryPayload[],
    ): Promise<GlossaryEntry[]> {
        return this.put(
            new URLSearchParams(),
            "glossary",
            JSON.stringify({ entries }),
        );
    }
}

export default ClientBrainService;
